import type { WorkflowConfigInput } from '@automata/wfb-api';
import sortBy from 'lodash/sortBy';

import { NULL_LABWARE_TYPE } from './constants';
import { err, ok } from './utils';
import { LabwareDefByDefURI } from '../../labware/types';
import type { LabwareProperties } from '../../types';
import { LabwareOffsetDefinition } from '../../zodSchemas/labwareOffsetDefinition';
import * as LabwareErrors from '../shared/labwareErrors';
import type {
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../shared/types';

export default function getJsonLabwareTypes(
  allDefinitions: LabwareDefByDefURI,
  labware: Record<string, LabwareProperties>,
  labwareOffsetDefinitions: Record<string, LabwareOffsetDefinition>
): MaybeWorkflowSerializationErrors<
  WorkflowConfigInput['workflow']['labware_types']
> {
  const errors: WorkflowSerializationError[] = [];
  const validLabwareTypes: WorkflowConfigInput['workflow']['labware_types'] =
    [];

  for (const l of sortBy(Object.values(labware), 'labwareDefinitionURI')) {
    const { id: labwareId, labwareDefinitionURI } = l;

    const definition =
      labwareDefinitionURI !== null
        ? allDefinitions[labwareDefinitionURI]
        : undefined;

    if (labwareDefinitionURI === null) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.MISSING_LABWARE_DEFINITION_URI,
        labwareId,
      });
    } else if (definition === undefined) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.INVALID_LABWARE_DEFINITION_URI,
        labwareId,
        labwareDefinitionURI,
      });
    } else {
      const labwareOffsetDefinition = labwareOffsetDefinitions[labwareId] || {};

      if (!validLabwareTypes.some(({ id }) => id === labwareId))
        validLabwareTypes.push({
          id: labwareId,
          dimensions: {
            height: definition.dimensions.zDimension,
            length: definition.dimensions.xDimension,
            width: definition.dimensions.yDimension,
          },
          ...labwareOffsetDefinition,
        });
    }
  }

  if (errors.length > 0) {
    return err(errors);
  }

  return ok(validLabwareTypes);
}

// TODO(AL, 10/07/2024): Loads of duplication here, consider refactoring
export function getJsonLabwareTypesForSave(
  allDefinitions: LabwareDefByDefURI,
  labware: Record<string, LabwareProperties>,
  labwareOffsetDefinitions: Record<string, LabwareOffsetDefinition>
): WorkflowConfigInput['workflow']['labware_types'] {
  const validLabwareTypes: WorkflowConfigInput['workflow']['labware_types'] =
    [];

  for (const l of sortBy(Object.values(labware), 'labwareDefinitionURI')) {
    const { labwareDefinitionURI } = l;
    const definition =
      labwareDefinitionURI !== null
        ? allDefinitions[labwareDefinitionURI]
        : undefined;

    const labwareTypeId = labwareDefinitionURI || NULL_LABWARE_TYPE;
    const labwareOffsetDefinition =
      labwareOffsetDefinitions[labwareTypeId] || {};

    if (!validLabwareTypes.some(({ id }) => id === labwareTypeId)) {
      validLabwareTypes.push({
        id: labwareTypeId,
        dimensions: {
          height: definition?.dimensions.zDimension,
          length: definition?.dimensions.xDimension,
          width: definition?.dimensions.yDimension,
        },
        ...labwareOffsetDefinition,
      });
    }
  }

  return validLabwareTypes;
}
